import { initializeFaro, getWebInstrumentations, LogLevel } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { prepareLabURL } from './constants';

const COLLECT_PATH = '/faro/collect';
const LAB_HOST = /^https?:\/\/([a-z0-9-]+)lab\./i;
const NON_ERROR_CONSOLE_LEVELS = [
  LogLevel.TRACE, LogLevel.DEBUG, LogLevel.INFO, LogLevel.LOG, LogLevel.WARN
];

let faroInstance = null;

/**
 * Kill switch: Faro/Alloy infra isn't deployed to any environment yet, so
 * this currently fires unconditionally against a backend with no /faro
 * route, surfacing as CORS/404 errors in the browser. Off by default until
 * infra confirms an environment is ready; flip REACT_APP_FARO_ENABLED to
 * 'true' per-environment once it is.
 */
export function isTelemetryEnabled () {
  return process.env.REACT_APP_FARO_ENABLED === 'true';
}

/** Lab origin from ?env= / ?lab= (or the build override); null on a bare landing page. */
export function resolveBackendOrigin (search = window.location.search) {
  const params = new URLSearchParams(search);
  const env = params.get('env');
  const lab = params.get('lab');
  if (!env && !lab && !process.env.REACT_APP_BACKEND_SERVER_URL) {
    return null;
  }
  return prepareLabURL(env, lab);
}

function resolveEnvironment (backendOrigin) {
  const match = typeof backendOrigin === 'string' ? backendOrigin.match(LAB_HOST) : null;
  return match ? match[1].toLowerCase() : 'prod';
}

function resolveCollectorUrl (backendOrigin) {
  if (process.env.REACT_APP_FARO_COLLECTOR_URL) {
    return process.env.REACT_APP_FARO_COLLECTOR_URL;
  }
  return backendOrigin ? `${backendOrigin}${COLLECT_PATH}` : null;
}

function escapeRegExp (value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripPageUrl (item) {
  const pageUrl = item?.meta?.page?.url;
  if (typeof pageUrl !== 'string') {
    return item;
  }
  const page = { ...item.meta.page, url: pageUrl.split(/[?#]/)[0] };
  return { ...item, meta: { ...item.meta, page } };
}

function buildInstrumentations (backendOrigin) {
  const instrumentations = getWebInstrumentations({
    captureConsole: true,
    captureConsoleDisabledLevels: NON_ERROR_CONSOLE_LEVELS
  });
  if (!backendOrigin) {
    return instrumentations;
  }
  const propagateTraceHeaderCorsUrls = [new RegExp(`^${escapeRegExp(backendOrigin)}(/|$)`)];
  instrumentations.push(new TracingInstrumentation({
    instrumentationOptions: { propagateTraceHeaderCorsUrls }
  }));
  return instrumentations;
}

export function initTelemetry ({ appVersion, search = window.location.search }) {
  if (faroInstance || !isTelemetryEnabled()) {
    return faroInstance;
  }
  const backendOrigin = resolveBackendOrigin(search);
  const url = resolveCollectorUrl(backendOrigin);
  if (!url) {
    return null;
  }
  try {
    faroInstance = initializeFaro({
      url,
      app: { name: 'login', version: appVersion, environment: resolveEnvironment(backendOrigin) },
      sessionTracking: { enabled: true, samplingRate: 1, persistent: false },
      ignoreErrors: [/ResizeObserver loop/, /^Script error\.?$/, /^canceled$/i],
      ignoreUrls: [url],
      beforeSend: stripPageUrl,
      instrumentations: buildInstrumentations(backendOrigin)
    });
  } catch (_error) {
    faroInstance = null;
  }
  return faroInstance;
}

export function resetTelemetryForTests () {
  faroInstance = null;
}
